import { bindActionsByKey } from '@/lib/valtio/bindActionsByKey'
import { triggerToast } from '@/shared/ui/toast'
import { isMouseEvent } from '@/utils/events'
import * as logger from '@/utils/logger'

import { getContentForRichtextCell } from '~shared/types/Cell'

import {
  createJSONContentFromHTMLContent,
  renderJSONContentAsPlaintext,
} from '@/features//richtext/tiptap.helpers'
import { getCell } from '@/features/rundown/data/cells.store'
import {
  gridSelectionActions,
  gridStateActions,
} from '@/features/rundown/grid/grid.store'
import { modalActions } from '@/features/rundown/modals/modal.store'

import {
  captureCellSelectionRect,
  captureTaskItemCaretCoords,
} from './cell-types/richtext/tiptap.focus'
import { upsertCell } from './cell.api'
import { type CellKey } from './cell.schema'
import { parseCellKey } from './cell.schema'

/**
 * Cached actions for a single cell.
 *
 * @see {@link bindActionsByKey}
 */
export const getCellActions = bindActionsByKey((cellKey: CellKey) => ({
  /**
   * Select this cell
   */
  select: () => {
    const { columnId, cueId } = parseCellKey(cellKey)
    gridSelectionActions.selectCell(cueId, columnId)
  },

  /**
   * Deselect this cell (ie. clear selection)
   */
  deselect: () => {
    gridSelectionActions.clear()
  },

  /**
   * Begin editing this cell
   *
   * @param event If event is from a mouse, cursor position and text selection
   *   captured and passed on to be used in the tiptap focus feature. If it is
   *   a key press on a task checkbox, the cursor goes to that task item.
   */
  startEditMode: (event?: React.BaseSyntheticEvent) => {
    const clickCoords =
      event && isMouseEvent(event) && event.type === 'dblclick'
        ? {
            x: event.clientX,
            y: event.clientY,
            selectionRect: captureCellSelectionRect(),
          }
        : event?.target instanceof HTMLInputElement &&
            event.target.type === 'checkbox'
          ? captureTaskItemCaretCoords(event.target)
          : null

    gridStateActions.setEditingCellAndClickEvent(cellKey, clickCoords)
  },

  /**
   * Copy cell's content to clipboard
   */
  copyContents() {
    const cell = getCell(cellKey)
    if (!cell) return false

    const contentAsHTML = getContentForRichtextCell(cell.content)

    const contentAsJSON = createJSONContentFromHTMLContent(contentAsHTML)

    const contentAsText = renderJSONContentAsPlaintext(contentAsJSON)

    // Write to clipboard
    // Note: All calls before this must be sync, otherwise it fails in Safari.
    navigator.clipboard
      .write([
        new ClipboardItem({
          'text/html': new Blob([contentAsHTML], { type: 'text/html' }),
          'text/plain': new Blob([contentAsText], { type: 'text/plain' }),
        }),
      ])
      .then(() => {
        triggerToast({
          type: 'success',
          title: `Cell contents copied.`,
          body: '',
          duration: 1500,
        })
      })
      .catch((err) => {
        logger.error('[CellAction] Copy failed for cell:', cellKey, err)

        triggerToast({
          type: 'fail',
          title: `Failed to copy cell contents.`,
          body: 'Please try again.',
        })
      })
  },

  /**
   * Start editing and paste clipboard content
   */
  pasteContents() {
    // Browser will prompt for permission first time
    const payloadPromise = readClipboardPayload()

    payloadPromise
      .then((payload) => {
        // Set state that tiptap editor will pick up on mount
        gridStateActions.setEditingCellAndPaste(cellKey, payload)
      })
      .catch((err) => {
        logger.error('[CellAction] Paste failed for cell:', cellKey, err)

        triggerToast({
          type: 'fail',
          title: `Failed to paste cell contents.`,
          body: 'Please try again.',
        })
      })
  },
  /**
   * Delete cell's content, with confirmation dialog
   */
  deleteContents() {
    const cell = getCell(cellKey)
    if (!cell) return false

    void modalActions
      .open('ALERT_DIALOG', {
        title: 'Delete cell content?',
        description: 'Content can be restored in cell history for 30 days.',
        variant: 'destructive',
        confirmLabel: 'Delete',
      })
      .then((didConfirm) => {
        if (!didConfirm) return

        logger.debug('[CellAction] Delete action confirmed for', cellKey)

        const { columnId, cueId } = parseCellKey(cellKey)

        const contentPayload = { content: { text: '' } } // Delete content

        upsertCell(cell, columnId, cueId, contentPayload)
          .then(() => {
            logger.info('[CellAction] Deleted content for', cellKey)
          })
          .catch((err: unknown) => {
            logger.error('[CellAction] Delete content failed for', cellKey, err)
            triggerToast({
              type: 'fail',
              title: `Failed to delete cell content.`,
              body: 'Please try again.',
            })
          })
      })
  },
}))

async function readClipboardPayload() {
  try {
    const items = await navigator.clipboard.read()
    let html = '',
      text = ''

    for (const item of items) {
      if (item.types.includes('text/html')) {
        const blob = await item.getType('text/html')
        html = await blob.text()
      }
      if (item.types.includes('text/plain')) {
        const blob = await item.getType('text/plain')
        text = await blob.text()
      }
    }
    return { html, text }
  } catch {
    // Firefox/Safari fallback: html read often needs extra permission,
    // degrade gracefully to plaintext.
    const text = await navigator.clipboard.readText().catch(() => '')
    return { html: '', text }
  }
}
